import {
	type ComponentProps,
	type CSSProperties,
	type ReactNode,
	type RefObject,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import { css, keyframes } from '@compiled/react';
import { type MessageDescriptor, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button-v25-3/new';
import AddIcon from '@atlaskit/icon/core/add';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import CommentIcon from '@atlaskit/icon/core/comment';
import CrossIcon from '@atlaskit/icon/core/cross';
import EditIcon from '@atlaskit/icon/core/edit';
import GlobeIcon from '@atlaskit/icon/core/globe';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import MicrophoneIcon from '@atlaskit/icon/core/microphone';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import { ConfluenceIcon } from '@atlaskit/logo';
import { Text } from '@atlaskit/primitives-next';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { type HelpArticleId } from '../../jsm-portal-playground-data';
import { jsmPortalPlaygroundMessages as portalMessages } from '../../jsm-portal-playground-messages';
import { jsmPortalPlaygroundRovoChatMessages as messages } from '../../jsm-portal-playground-rovo-chat-messages';
import { useJsmPlaygroundAnalytics } from '../../use-jsm-playground-analytics';
import { EASE_OUT, usePrefersReducedMotion } from '../jsm-playground-motion';
import { JsmPlaygroundRovoMorphingShape } from '../jsm-playground-rovo-morphing-shape';

type IconButtonIcon = NonNullable<ComponentProps<typeof IconButton>['icon']>;

const NewChatButtonIcon: IconButtonIcon = () => <EditIcon label="" size="medium" />;
const CloseButtonIcon: IconButtonIcon = () => <CrossIcon label="" size="medium" />;
const GoodAnswerButtonIcon: IconButtonIcon = () => <ThumbsUpIcon label="" />;
const BadAnswerButtonIcon: IconButtonIcon = () => <ThumbsDownIcon label="" />;
const FeedbackButtonIcon: IconButtonIcon = () => <CommentIcon label="" />;
const AddContextButtonIcon: IconButtonIcon = () => <AddIcon label="" />;
const DictateButtonIcon: IconButtonIcon = () => <MicrophoneIcon label="" />;

const noop = () => {};

type AnswerBlock =
	| { kind: 'paragraph'; text: MessageDescriptor }
	| { kind: 'heading'; text: MessageDescriptor }
	| {
			kind: 'linkedParagraph';
			before: MessageDescriptor;
			linkLabel: MessageDescriptor;
			after: MessageDescriptor;
	  }
	| { kind: 'bullets'; items: MessageDescriptor[] };

type RovoArticleConversation = {
	visitorQuestion: MessageDescriptor;
	blocks: AnswerBlock[];
};

const VPN_ANSWER_BLOCKS: AnswerBlock[] = [
	{ kind: 'paragraph', text: messages.answerIntro },
	{ kind: 'heading', text: messages.answerStep1Heading },
	{
		kind: 'linkedParagraph',
		before: messages.answerStep1Before,
		linkLabel: messages.answerStep1Link,
		after: messages.answerStep1After,
	},
	{ kind: 'heading', text: messages.answerStep2Heading },
	{ kind: 'bullets', items: [messages.answerStep2Bullet1, messages.answerStep2Bullet2] },
	{ kind: 'heading', text: messages.answerStep3Heading },
	{ kind: 'bullets', items: [messages.answerStep3Bullet1, messages.answerStep3Bullet2] },
	{ kind: 'paragraph', text: messages.answerOutro },
];

const LAPTOP_ANSWER_BLOCKS: AnswerBlock[] = [
	{ kind: 'paragraph', text: messages.laptopAnswerIntro },
	{ kind: 'heading', text: messages.laptopAnswerStep1Heading },
	{ kind: 'paragraph', text: messages.laptopAnswerStep1Body },
	{ kind: 'heading', text: messages.laptopAnswerStep2Heading },
	{
		kind: 'bullets',
		items: [messages.laptopAnswerStep2Bullet1, messages.laptopAnswerStep2Bullet2],
	},
	{ kind: 'heading', text: messages.laptopAnswerStep3Heading },
	{
		kind: 'bullets',
		items: [messages.laptopAnswerStep3Bullet1],
	},
	{ kind: 'paragraph', text: messages.laptopAnswerOutro },
];

const PASSWORD_ANSWER_BLOCKS: AnswerBlock[] = [
	{ kind: 'paragraph', text: messages.passwordAnswerIntro },
	{ kind: 'heading', text: messages.passwordAnswerStep1Heading },
	{ kind: 'paragraph', text: messages.passwordAnswerStep1Body },
	{ kind: 'heading', text: messages.passwordAnswerStep2Heading },
	{
		kind: 'bullets',
		items: [messages.passwordAnswerStep2Bullet1, messages.passwordAnswerStep2Bullet2],
	},
	{ kind: 'heading', text: messages.passwordAnswerStep3Heading },
	{
		kind: 'bullets',
		items: [messages.passwordAnswerStep3Bullet1],
	},
	{ kind: 'paragraph', text: messages.passwordAnswerOutro },
];

const ROVO_CONVERSATIONS: Record<HelpArticleId, RovoArticleConversation> = {
	vpn: {
		visitorQuestion: messages.visitorQuestion,
		blocks: VPN_ANSWER_BLOCKS,
	},
	laptop: {
		visitorQuestion: messages.laptopVisitorQuestion,
		blocks: LAPTOP_ANSWER_BLOCKS,
	},
	password: {
		visitorQuestion: messages.passwordVisitorQuestion,
		blocks: PASSWORD_ANSWER_BLOCKS,
	},
};

const THINKING_MS = 2200;
const BLOCK_MS = 420;
const AFTERWORD_MS = 500;

export const ROVO_VPN_ANSWER_STREAM_MS =
	THINKING_MS + (VPN_ANSWER_BLOCKS.length - 1) * BLOCK_MS + AFTERWORD_MS;

const EXPAND_MS = 320;
const PANEL_EASE = 'cubic-bezier(0.33, 0, 0.2, 1)';

const LAUNCHER_SIZE = 40;
const LAUNCHER_INSET = 16;
const LAUNCHER_GRADIENT = 'linear-gradient(135deg, #E1EF8F 0%, #94E7D6 100%)';
const PANEL_WIDTH = 400;

type PanelPhase = 'collapsed' | 'expanded' | 'closing';

const usePanelExpansion = (onClose: () => void, shouldShowComplete: boolean) => {
	const [phase, setPhase] = useState<PanelPhase>(shouldShowComplete ? 'expanded' : 'collapsed');
	const closeTimerRef = useRef<number | null>(null);

	useEffect(() => {
		if (shouldShowComplete) {
			setPhase('expanded');
			return;
		}
		const frame = requestAnimationFrame(() => setPhase('expanded'));
		return () => cancelAnimationFrame(frame);
	}, [shouldShowComplete]);

	useEffect(
		() => () => {
			if (closeTimerRef.current !== null) {
				window.clearTimeout(closeTimerRef.current);
			}
		},
		[],
	);

	const requestClose = () => {
		if (shouldShowComplete) {
			onClose();
			return;
		}
		setPhase('closing');
		closeTimerRef.current = window.setTimeout(onClose, EXPAND_MS);
	};

	const isOpen = phase === 'expanded';
	const transition = (value: string) => (shouldShowComplete ? undefined : value);

	return {
		requestClose,
		panelStyle: {
			borderRadius: isOpen ? token('radius.large') : `${LAUNCHER_SIZE / 2}px`,
			top: isOpen ? `${LAUNCHER_INSET}px` : `calc(100% - ${LAUNCHER_INSET + LAUNCHER_SIZE}px)`,
			width: isOpen ? `${PANEL_WIDTH}px` : `${LAUNCHER_SIZE}px`,
			transition: transition(
				`top ${EXPAND_MS}ms ${PANEL_EASE}, width ${EXPAND_MS}ms ${PANEL_EASE}, border-radius ${EXPAND_MS}ms ${PANEL_EASE}`,
			),
		} satisfies CSSProperties,
		fillStyle: {
			opacity: isOpen ? 0 : 1,
			transition: transition(`opacity 180ms ${PANEL_EASE}`),
		} satisfies CSSProperties,
		contentStyle: {
			opacity: isOpen ? 1 : 0,
			transition: transition(
				`opacity ${EXPAND_MS / 2}ms ${PANEL_EASE} ${isOpen ? EXPAND_MS / 2 : 0}ms`,
			),
		} satisfies CSSProperties,
		glyphStyle: {
			opacity: isOpen ? 0 : 1,
			transition: transition(`opacity 140ms ${PANEL_EASE}`),
		} satisfies CSSProperties,
	};
};

const useAnswerStream = (blocks: AnswerBlock[], shouldRevealImmediately: boolean) => {
	const [revealedCount, setRevealedCount] = useState(shouldRevealImmediately ? blocks.length : 0);
	const [isComplete, setIsComplete] = useState(shouldRevealImmediately);

	useEffect(() => {
		if (shouldRevealImmediately) {
			setRevealedCount(blocks.length);
			setIsComplete(true);
			return;
		}
		setRevealedCount(0);
		setIsComplete(false);
		const timers = blocks.map((_, index) =>
			window.setTimeout(() => setRevealedCount(index + 1), THINKING_MS + index * BLOCK_MS),
		);
		timers.push(
			window.setTimeout(
				() => setIsComplete(true),
				THINKING_MS + (blocks.length - 1) * BLOCK_MS + AFTERWORD_MS,
			),
		);
		return () => timers.forEach(window.clearTimeout);
	}, [blocks, shouldRevealImmediately]);

	return { isThinking: revealedCount === 0, revealedCount, isComplete };
};

type ChatCloseOptions = {
	shouldRestoreFocus: boolean;
};

export const usePortalRovoChat = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [isOpenedByDemo, setIsOpenedByDemo] = useState(false);
	const [shouldStartComplete, setShouldStartComplete] = useState(false);
	const [returnFocusTo, setReturnFocusTo] = useState<RefObject<HTMLElement> | null>(null);
	const launcherRef = useRef<HTMLButtonElement>(null);
	const { firePlaygroundScreenNavigated } = useJsmPlaygroundAnalytics('it_support');

	useEffect(() => {
		if (isOpen || !returnFocusTo) {
			return;
		}
		returnFocusTo.current?.focus();
		setReturnFocusTo(null);
	}, [isOpen, returnFocusTo]);

	const open = useCallback(
		(opener: RefObject<HTMLElement> = launcherRef, { isDemo = false } = {}) => {
			setReturnFocusTo(opener);
			setIsOpenedByDemo(isDemo);
			setShouldStartComplete(false);
			setIsOpen(true);
			if (!isDemo) {
				firePlaygroundScreenNavigated({
					team_type: 'it_support',
					screen_id: 'virtual_agent_chat',
					interaction_mode: 'manual_click',
				});
			}
		},
		[firePlaygroundScreenNavigated],
	);

	const close = useCallback(({ shouldRestoreFocus }: ChatCloseOptions) => {
		if (!shouldRestoreFocus) {
			setReturnFocusTo(null);
		}
		setIsOpen(false);
		setShouldStartComplete(false);
	}, []);

	const completeNow = useCallback(() => {
		setReturnFocusTo(null);
		setIsOpenedByDemo(true);
		setShouldStartComplete(true);
		setIsOpen(true);
	}, []);

	return { isOpen, isOpenedByDemo, shouldStartComplete, launcherRef, open, close, completeNow };
};

type PortalRovoChat = ReturnType<typeof usePortalRovoChat>;

export const JsmPortalPlaygroundRovoLauncher = ({
	chat,
	articleId = 'vpn',
	onStreamComplete,
}: {
	chat: PortalRovoChat;
	articleId?: HelpArticleId;
	onStreamComplete?: () => void;
}) => {
	const intl = useIntl();

	if (chat.isOpen) {
		return (
			<RovoChatPanel
				articleId={articleId}
				onClose={chat.close}
				onStreamComplete={chat.isOpenedByDemo ? onStreamComplete : undefined}
				shouldFocusOnOpen={!chat.isOpenedByDemo}
				shouldStartComplete={chat.shouldStartComplete}
			/>
		);
	}

	return (
		<button
			ref={chat.launcherRef}
			type="button"
			css={launcherButtonStyles}
			aria-label={intl.formatMessage(portalMessages.askRovo)}
			onClick={() => chat.open()}
		>
			<AiChatIcon label="" color={token('color.text')} />
		</button>
	);
};

export const JsmPortalPlaygroundRovoLauncherMark = () => (
	<span css={launcherButtonStyles} aria-hidden="true">
		<AiChatIcon label="" color={token('color.text')} />
	</span>
);

const RovoChatPanel = ({
	articleId = 'vpn',
	onClose,
	onStreamComplete,
	shouldFocusOnOpen,
	shouldStartComplete = false,
}: {
	articleId?: HelpArticleId;
	onClose: (options: ChatCloseOptions) => void;
	onStreamComplete?: () => void;
	shouldFocusOnOpen: boolean;
	shouldStartComplete?: boolean;
}) => {
	const intl = useIntl();
	const prefersReducedMotion = usePrefersReducedMotion();
	const shouldShowComplete = prefersReducedMotion || shouldStartComplete;
	const panelRef = useRef<HTMLDivElement>(null);
	const closeButtonRef = useRef<HTMLButtonElement>(null);
	const conversation = ROVO_CONVERSATIONS[articleId] ?? ROVO_CONVERSATIONS.vpn;
	const { isThinking, revealedCount, isComplete } = useAnswerStream(
		conversation.blocks,
		shouldShowComplete,
	);
	const onStreamCompleteRef = useRef(onStreamComplete);
	onStreamCompleteRef.current = onStreamComplete;
	useEffect(() => {
		if (isComplete) {
			onStreamCompleteRef.current?.();
		}
	}, [isComplete]);
	const { requestClose, panelStyle, fillStyle, contentStyle, glyphStyle } = usePanelExpansion(
		() =>
			onClose({
				shouldRestoreFocus: panelRef.current?.contains(document.activeElement) ?? false,
			}),
		shouldShowComplete,
	);
	const requestCloseRef = useRef(requestClose);
	requestCloseRef.current = requestClose;
	const { fireTextFieldBlurred } = useJsmPlaygroundAnalytics('it_support');

	useLayoutEffect(() => {
		if (shouldStartComplete) {
			closeButtonRef.current?.focus();
		}
	}, [shouldStartComplete]);

	useEffect(() => {
		if (shouldFocusOnOpen) {
			closeButtonRef.current?.focus();
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			const playground = panelRef.current?.closest('[data-preview-scale]') ?? panelRef.current;
			if (
				event.key === 'Escape' &&
				event.target instanceof Node &&
				playground?.contains(event.target)
			) {
				requestCloseRef.current();
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, []);

	const answerAnnouncement = conversation.blocks
		.flatMap((block) => {
			if (block.kind === 'bullets') {
				return block.items.map((item) => intl.formatMessage(item));
			}
			if (block.kind === 'linkedParagraph') {
				return [
					`${intl.formatMessage(block.before)} ${intl.formatMessage(
						block.linkLabel,
					)} ${intl.formatMessage(block.after)}`,
				];
			}
			return [intl.formatMessage(block.text)];
		})
		.join(' ');

	return (
		<div
			ref={panelRef}
			css={panelStyles}
			style={panelStyle}
			role="dialog"
			aria-label={intl.formatMessage(messages.chatLabel)}
			data-testid="jsm-portal-rovo-chat"
		>
			<span css={launcherFillStyles} style={fillStyle} aria-hidden="true" />
			<span css={launcherGlyphStyles} style={glyphStyle} aria-hidden="true">
				<AiChatIcon label="" color={token('color.text')} />
			</span>

			<div css={panelContentStyles} style={contentStyle}>
				<div css={headerStyles}>
					<div css={headerNameStyles}>
						<span css={avatarStyles} aria-hidden="true">
							{intl.formatMessage(messages.chatName).charAt(0)}
						</span>
						<Text weight="semibold" maxLines={1}>
							{intl.formatMessage(messages.chatName)}
						</Text>
						<ChevronDownIcon label="" size="small" color={token('color.icon.subtle')} />
					</div>
					<div css={headerActionsStyles}>
						<InertControls>
							<IconButton
								appearance="subtle"
								icon={NewChatButtonIcon}
								label={intl.formatMessage(messages.newChat)}
								onClick={noop}
								spacing="compact"
								tabIndex={-1}
							/>
						</InertControls>
						<IconButton
							ref={closeButtonRef}
							appearance="subtle"
							icon={CloseButtonIcon}
							label={intl.formatMessage(messages.closeChat)}
							onClick={requestClose}
							spacing="compact"
						/>
					</div>
				</div>

				<div css={transcriptStyles}>
					<div css={questionRowStyles}>
						<div css={questionBubbleStyles}>
							<Text size="medium" color="color.text.inverse">
								{intl.formatMessage(conversation.visitorQuestion)}
							</Text>
						</div>
					</div>

					{isThinking ? (
						<div css={thinkingRowStyles}>
							<JsmPlaygroundRovoMorphingShape />
							<Text size="small" color="color.text.subtlest">
								{intl.formatMessage(messages.thinking)}
							</Text>
						</div>
					) : (
						<div css={answerStyles} aria-hidden="true">
							{conversation.blocks.slice(0, revealedCount).map((block, index) => (
								<div key={index} css={blockInStyles}>
									<AnswerBlockBody block={block} />
								</div>
							))}

							{isComplete && (
								<div css={[blockInStyles, afterwordStyles]}>
									<span css={sourcesChipStyles}>
										<ConfluenceIcon
											appearance="brand"
											label=""
											shouldUseNewLogoDesign
											size="xxsmall"
										/>
										<Text size="small">{intl.formatMessage(messages.sources)}</Text>
									</span>
									<InertControls>
										<IconButton
											appearance="subtle"
											icon={GoodAnswerButtonIcon}
											label={intl.formatMessage(messages.goodAnswer)}
											onClick={noop}
											spacing="compact"
											tabIndex={-1}
										/>
										<IconButton
											appearance="subtle"
											icon={BadAnswerButtonIcon}
											label={intl.formatMessage(messages.badAnswer)}
											onClick={noop}
											spacing="compact"
											tabIndex={-1}
										/>
										<IconButton
											appearance="subtle"
											icon={FeedbackButtonIcon}
											label={intl.formatMessage(messages.giveFeedback)}
											onClick={noop}
											spacing="compact"
											tabIndex={-1}
										/>
									</InertControls>
								</div>
							)}
						</div>
					)}
				</div>

				<div css={footerStyles}>
					<div css={composerStyles}>
						<input
							css={composerInputStyles}
							type="text"
							aria-label={intl.formatMessage(messages.askFollowUp)}
							placeholder={intl.formatMessage(messages.askFollowUp)}
							onBlur={(event) => {
								const val = event.target.value;
								fireTextFieldBlurred({
									screenId: 'virtual_agent_chat',
									elementId: 'askFollowUp',
									actionSubjectId: 'askFollowUp',
									hasChanged: val.length > 0,
									inputLength: val.length,
									teamType: 'it_support',
								});
							}}
						/>
						<div css={composerToolbarStyles}>
							<div css={composerToolbarGroupStyles}>
								<InertControls>
									<IconButton
										appearance="subtle"
										icon={AddContextButtonIcon}
										label={intl.formatMessage(messages.addContext)}
										onClick={noop}
										spacing="compact"
										tabIndex={-1}
									/>
								</InertControls>
								<span css={appMarksStyles} aria-hidden="true">
									<SlackMark />
									<ConfluenceIcon
										appearance="brand"
										label=""
										shouldUseNewLogoDesign
										size="xxsmall"
									/>
									<Text size="small" color="color.text.subtlest">
										{intl.formatMessage(messages.moreApps)}
									</Text>
								</span>
							</div>
							<div css={composerToolbarGroupStyles}>
								<span css={agentPickerStyles} aria-hidden="true">
									<AiAgentIcon label="" size="small" color={token('color.icon.subtle')} />
									<Text size="small" color="color.text.subtle">
										{intl.formatMessage(messages.agentPicker)}
									</Text>
								</span>
								{isComplete ? (
									<InertControls>
										<IconButton
											appearance="subtle"
											icon={DictateButtonIcon}
											label={intl.formatMessage(messages.dictate)}
											onClick={noop}
											spacing="compact"
											tabIndex={-1}
										/>
									</InertControls>
								) : (
									<span css={stopButtonStyles} aria-hidden="true">
										<span css={stopSquareStyles} />
									</span>
								)}
							</div>
						</div>
					</div>

					<div css={disclosureStyles}>
						<InformationCircleIcon label="" size="small" color={token('color.icon.subtle')} />
						<Text size="small" color="color.text.subtlest">
							{intl.formatMessage(messages.disclosure)}
						</Text>
						<LinkExternalIcon label="" size="small" color={token('color.icon.subtle')} />
					</div>
				</div>
			</div>

			<VisuallyHidden>
				<span aria-live="polite">{isComplete ? answerAnnouncement : ''}</span>
			</VisuallyHidden>
		</div>
	);
};

const InertControls = ({ children }: { children: ReactNode }) => (
	<span
		css={inertControlsStyles}
		aria-hidden="true"
		// @ts-expect-error -- inert is valid HTML but is missing from @compiled/react types
		inert=""
	>
		{children}
	</span>
);

const AnswerBlockBody = ({ block }: { block: AnswerBlock }) => {
	const intl = useIntl();

	if (block.kind === 'heading') {
		return (
			<Text size="medium" weight="bold">
				{intl.formatMessage(block.text)}
			</Text>
		);
	}

	if (block.kind === 'bullets') {
		return (
			<ul css={bulletsStyles}>
				{block.items.map((item) => (
					<li key={item.id}>
						<Text size="medium">{intl.formatMessage(item)}</Text>
					</li>
				))}
			</ul>
		);
	}

	if (block.kind === 'linkedParagraph') {
		return (
			<Text size="medium">
				{`${intl.formatMessage(block.before)} `}
				<span css={resourceChipStyles}>
					<GlobeIcon label="" size="small" color={token('color.link')} />
					<Text size="medium" color="color.link">
						{intl.formatMessage(block.linkLabel)}
					</Text>
				</span>
				{` ${intl.formatMessage(block.after)}`}
			</Text>
		);
	}

	return <Text size="medium">{intl.formatMessage(block.text)}</Text>;
};

const SlackMark = () => (
	<svg
		width={16}
		height={16}
		viewBox="23.53 23.8 33.35 33.35"
		aria-hidden="true"
		css={slackMarkStyles}
	>
		<path
			fill="#E01E5A"
			fillRule="evenodd"
			d="M30.5674 44.8842C30.5674 46.8232 29.0034 48.3881 27.0656 48.3881C25.1277 48.3881 23.5637 46.8179 23.5637 44.8842C23.5637 42.9505 25.1277 41.3804 27.0656 41.3804H30.5674V44.8842ZM32.3157 44.8842C32.3157 42.9452 33.8796 41.3804 35.8175 41.3804C37.7554 41.3804 39.3193 42.9452 39.3193 44.8842V53.6412C39.3193 55.5802 37.7554 57.145 35.8175 57.145C33.8796 57.145 32.3157 55.5802 32.3157 53.6412V44.8842Z"
		/>
		<path
			fill="#36C5F0"
			fillRule="evenodd"
			d="M35.8175 30.8153C33.8796 30.8153 32.3156 29.2504 32.3156 27.3115C32.3156 25.3725 33.8796 23.8076 35.8175 23.8076C37.7553 23.8076 39.3193 25.3725 39.3193 27.3115V30.8153H35.8175ZM35.8175 32.5909C37.7553 32.5909 39.3193 34.1558 39.3193 36.0948C39.3193 38.0338 37.7553 39.5986 35.8175 39.5986H27.0392C25.1013 39.5986 23.5374 38.0285 23.5374 36.0948C23.5374 34.1611 25.1013 32.5909 27.0392 32.5909H35.8175Z"
		/>
		<path
			fill="#2EB67D"
			fillRule="evenodd"
			d="M49.8472 36.0948C49.8472 34.1558 51.4111 32.5909 53.349 32.5909C55.2869 32.5909 56.8508 34.1558 56.8508 36.0948C56.8508 38.0338 55.2869 39.5986 53.349 39.5986H49.8472V36.0948ZM48.0989 36.0948C48.0989 38.0338 46.5349 39.5986 44.597 39.5986C42.6592 39.5986 41.0952 38.0338 41.0952 36.0948V27.3115C41.0952 25.3725 42.6592 23.8076 44.597 23.8076C46.5349 23.8076 48.0989 25.3725 48.0989 27.3115V36.0948Z"
		/>
		<path
			fill="#ECB22E"
			fillRule="evenodd"
			d="M44.597 50.1373C46.5349 50.1373 48.0989 51.7022 48.0989 53.6412C48.0989 55.5802 46.5349 57.145 44.597 57.145C42.6592 57.145 41.0952 55.5802 41.0952 53.6412V50.1373H44.597ZM44.597 48.3881C42.6592 48.3881 41.0952 46.8232 41.0952 44.8842C41.0952 42.9452 42.6592 41.3804 44.597 41.3804H53.3753C55.3132 41.3804 56.8772 42.9452 56.8772 44.8842C56.8772 46.8232 55.3132 48.3881 53.3753 48.3881H44.597Z"
		/>
	</svg>
);

const blockIn = keyframes({
	from: { opacity: 0, transform: 'translateY(6px)' },
	to: { opacity: 1, transform: 'none' },
});

const launcherButtonStyles = css({
	position: 'absolute',
	insetBlockEnd: `${LAUNCHER_INSET}px`,
	insetInlineEnd: `${LAUNCHER_INSET}px`,
	zIndex: 100,
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: `${LAUNCHER_SIZE}px`,
	height: `${LAUNCHER_SIZE}px`,
	boxSizing: 'border-box',
	padding: token('space.100'),
	borderStyle: 'none',
	borderRadius: token('radius.full'),
	cursor: 'pointer',
	backgroundImage: LAUNCHER_GRADIENT,
	boxShadow: token('elevation.shadow.overlay'),
});

const panelStyles = css({
	position: 'absolute',
	insetBlockEnd: `${LAUNCHER_INSET}px`,
	insetInlineEnd: `${LAUNCHER_INSET}px`,
	zIndex: 200,
	display: 'flex',
	flexDirection: 'column',
	boxSizing: 'border-box',
	overflow: 'hidden',
	backgroundColor: token('elevation.surface.overlay'),
	boxShadow: token('elevation.shadow.overlay'),
});

const launcherFillStyles = css({
	position: 'absolute',
	inset: 0,
	pointerEvents: 'none',
	backgroundImage: LAUNCHER_GRADIENT,
});

const launcherGlyphStyles = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: `${LAUNCHER_SIZE}px`,
	height: '100%',
	pointerEvents: 'none',
});

const panelContentStyles = css({
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
	flexShrink: 0,
	width: `${PANEL_WIDTH}px`,
	height: '100%',
	minHeight: 0,
	font: token(
		'font.body',
		'normal 400 14px/20px "Atlassian Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
	),
});

const headerStyles = css({
	display: 'flex',
	alignItems: 'center',
	flexShrink: 0,
	gap: token('space.050'),
	paddingBlock: token('space.100'),
	paddingInline: token('space.150'),
});

const headerNameStyles = css({
	display: 'flex',
	alignItems: 'center',
	flexGrow: 1,
	gap: token('space.075'),
	minWidth: 0,
});

const headerActionsStyles = css({
	display: 'flex',
	alignItems: 'center',
	flexShrink: 0,
});

const avatarStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	width: '20px',
	height: '20px',
	borderRadius: token('radius.full'),
	backgroundColor: token('color.background.accent.green.bolder'),
	color: token('color.text.inverse'),
	font: token('font.body.small'),
	fontWeight: token('font.weight.bold'),
});

const inertControlsStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.025'),
});

const transcriptStyles = css({
	display: 'flex',
	flexDirection: 'column',
	flexGrow: 1,
	gap: token('space.200'),
	minHeight: 0,
	overflowY: 'auto',
	overflowX: 'hidden',
	paddingBlock: token('space.150'),
	paddingInline: token('space.200'),
});

const questionRowStyles = css({
	display: 'flex',
	justifyContent: 'flex-end',
	width: '100%',
});

const questionBubbleStyles = css({
	boxSizing: 'border-box',
	maxWidth: '100%',
	paddingBlock: token('space.100'),
	paddingInline: token('space.150'),
	borderRadius: token('radius.large'),
	backgroundColor: token('color.background.brand.bold'),
	whiteSpace: 'nowrap',
});

const thinkingRowStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
});

const answerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.150'),
	width: '100%',
});

const blockInStyles = css({
	animation: `${blockIn} 260ms ${EASE_OUT}`,
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
	},
});

const afterwordStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: token('space.150'),
});

const bulletsStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.150'),
	margin: 0,
	paddingInlineStart: token('space.250'),
	fontSize: '14px',
	lineHeight: '20px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& li': {
		padding: 0,
		fontSize: '14px',
		lineHeight: '20px',
	},
	// The prototype app's base reset adds this between items, on top of the list gap.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& li + li': {
		marginBlockStart: token('space.050'),
	},
});

const resourceChipStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.050'),
	paddingBlock: token('space.025'),
	paddingInline: token('space.050'),
	borderRadius: token('radius.small'),
	backgroundColor: token('color.background.selected'),
	verticalAlign: 'baseline',
});

const sourcesChipStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.075'),
	paddingBlock: token('space.050'),
	paddingInline: token('space.100'),
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.small'),
});

const footerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	flexShrink: 0,
	gap: token('space.050'),
	paddingBlockEnd: token('space.100'),
	paddingInline: token('space.150'),
});

const composerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	boxSizing: 'border-box',
	width: '100%',
	paddingBlock: token('space.100'),
	paddingInline: token('space.150'),
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.medium'),
	backgroundColor: token('elevation.surface'),
});

const composerInputStyles = css({
	width: '100%',
	paddingBlock: token('space.025'),
	paddingInline: 0,
	fontSize: '12.5px',
	borderStyle: 'none',
	outline: 'none',
	color: token('color.text'),
	backgroundColor: 'transparent',
	'&::placeholder': {
		color: token('color.text.subtlest'),
	},
});

const composerToolbarStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.100'),
});

const composerToolbarGroupStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.075'),
});

const appMarksStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.075'),
});

const slackMarkStyles = css({
	display: 'block',
	flexShrink: 0,
});

const agentPickerStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.050'),
});

const stopButtonStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	width: '24px',
	height: '24px',
	boxSizing: 'border-box',
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.small'),
});

const stopSquareStyles = css({
	width: '10px',
	height: '10px',
	borderRadius: token('radius.xsmall'),
	backgroundColor: token('color.background.danger.bold'),
});

const disclosureStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: token('space.050'),
});
